import Seo from "@/components/Seo";

/**
 * Shared shell for static policy / legal pages (Terms, Privacy, Shipping…).
 * Provides the editorial "overline · big serif title · body" chrome that
 * matches About.js so all policy pages feel like siblings, and wires up
 * SEO tags + a stable `data-testid` per page for the testing agent.
 *
 * Usage:
 *   <PolicyPage
 *     testId="terms-page"
 *     overline="Legal"
 *     title="Terms of Service"
 *     updated="February 15, 2026"
 *     description="Read the Luxsova Terms of Service."
 *   >
 *     <h2>1. Section</h2>
 *     <p>…</p>
 *   </PolicyPage>
 */
const PolicyPage = ({
  testId,
  overline,
  title,
  updated,
  description,
  children,
}) => (
  <>
    <Seo title={title} description={description} />
    <div className="lux-container pt-16 md:pt-24 pb-24 max-w-3xl" data-testid={testId}>
      <p className="overline text-neutral-500 mb-3">{overline}</p>
      <h1 className="font-serif-display text-4xl md:text-6xl leading-tight">
        {title}
      </h1>
      {updated && (
        <p className="text-xs uppercase tracking-[0.2em] text-neutral-400 mt-4">
          Last updated · {updated}
        </p>
      )}
      <div
        className="policy-body mt-10 text-neutral-700 leading-relaxed space-y-6
          [&>h2]:font-serif-display [&>h2]:text-2xl [&>h2]:md:text-3xl
          [&>h2]:text-black [&>h2]:mt-12 [&>h2]:mb-3
          [&>h3]:overline [&>h3]:text-neutral-500 [&>h3]:mt-8 [&>h3]:mb-2
          [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:space-y-2
          [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:space-y-2
          [&_a]:underline [&_a]:underline-offset-4 [&_a]:hover:opacity-60"
      >
        {children}
      </div>
    </div>
  </>
);

export default PolicyPage;
